import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Alert,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close"; // Import the close icon
import { Link } from "react-router-dom";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined"; // Import icon for the button
import {
  fonts,
  headingFonts,
  mainButtonStyles,
  noOutline,
  textFieldStyles,
} from "../../Styles/Styles";
import OfferStatusModal from "./OfferStatusModal";

const MakeOfferModal = ({ open, onClose, uniqueId, minOfferAmount }) => {
  const [offerAmount, setOfferAmount] = useState("");
  const [offerMessage, setOfferMessage] = useState("");
  const [offersCount, setOffersCount] = useState(0); // State to store only the count of offers
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false); // State to track loading
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: "",
    message: "",
    adID: null,
  });

  const user = JSON.parse(localStorage.getItem("user")); // Get the logged-in user

  const fetchOffersLength = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/offers/ad/${uniqueId}`
      );
      if (!response.ok) throw new Error("Failed to fetch offers");

      const data = await response.json();
      setOffersCount(data.length); // Set only the length of the offers array
    } catch (error) {
      setError(error.message);
    }
  };

  useEffect(() => {
    fetchOffersLength();
  }, [uniqueId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation: Check if the offer amount is valid
    if (!offerAmount || offerAmount < minOfferAmount) {
      setError(
        `Offer amount must be greater than the minimum offer amount ${minOfferAmount}`
      );
      return;
    }

    setLoading(true); // Set loading to true when submitting

    const offerData = {
      uniqueId: uniqueId,
      userId: user._id,
      offerAmount,
      offerMessage,
      email: user.email,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/offers`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(offerData),
        }
      );

      if (!response.ok) {
        setStatusModal({
          open: true,
          type: "error",
          message: error.message || "Something went wrong. Please try again.",
        });
      }

      setStatusModal({
        open: true,
        type: "success",
        message: "Offer submitted successfully!",
      });

      setOfferAmount("");
      setOfferMessage("");
    } catch (error) {
      setStatusModal({
        open: true,
        type: "error",
        message: error.message || "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false); // Set loading to false when the request is complete
    }
    fetchOffersLength();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs" // Adjust modal width as needed
      fullWidth
    >
      <DialogTitle sx={{ ...headingFonts }}>
        Make an Offer
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, ...noOutline }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ mb: 1 }}>
        <Typography variant="body1" sx={{ ...headingFonts }}>
          Offers Received ({offersCount})
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            label="Offer Amount"
            type="number"
            variant="outlined"
            fullWidth
            value={offerAmount}
            onChange={(e) => setOfferAmount(e.target.value)}
            sx={{
              mb: 2,
              mt: 2,
              ...textFieldStyles,
            }}
            required
            error={offerAmount && offerAmount < minOfferAmount + 1}
          />
          <TextField
            label="Message (optional)"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            value={offerMessage}
            onChange={(e) => setOfferMessage(e.target.value)}
            sx={{
              mb: 2,
              ...textFieldStyles,
            }}
          />
          <Button
            type="submit"
            className="main-button-style"
            sx={{
              mt: 2,
              ...mainButtonStyles,
            }}
            disabled={loading} // Disable the button while loading
          >
            {loading ? (
              <>
                <span>Submitting...</span>
                <CircularProgress size={20} sx={{ color: "white" }} />
              </>
            ) : (
              <>
                <span>Submit Offer</span>
                <ArrowForwardOutlinedIcon fontSize="small" />
              </>
            )}
          </Button>
        </form>
        {!user && (
          <Alert severity="error" sx={{ mt: 2, ...fonts }}>
            <Link to="/get-started" sx={{ mt: 2, ...fonts }}>
              Login
            </Link>{" "}
            to Make an Offer
          </Alert>
        )}
      </DialogContent>
      <OfferStatusModal
        open={statusModal.open}
        type={statusModal.type}
        message={statusModal.message}
        setStatusModal={setStatusModal}
      />
    </Dialog>
  );
};

export default MakeOfferModal;
